// hooks/useKeyboardAwareBar.js
import { useEffect, useRef } from "react";

export function useKeyboardAwareBar() {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = document.documentElement;

    const isKbOpen = () => {
      const vv = window.visualViewport;
      if (!vv) return false;
      const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      return overlap > 0;
    };

    const updateOffset = () => {
      const vv = window.visualViewport;
      if (!vv) return;
      const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty("--kb-offset", `${overlap}px`);
    };

    const ensureFixed = () => {
      if (!ref.current) return;
      ref.current.classList.add("is-focusin");
      updateOffset();
    };

    const maybeUnfix = () => {
      // Chỉ hạ footer nếu bàn phím thực sự đã đóng
      if (isKbOpen()) {
        updateOffset();
        return;
      }
      if (!ref.current) return;
      ref.current.classList.remove("is-focusin");
      root.style.removeProperty("--kb-offset");
    };

    const onFocusIn = (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches("input, textarea, [contenteditable='true'], select")) {
        // Đợi 1 frame để iOS cập nhật visualViewport trước khi tính overlap
        requestAnimationFrame(ensureFixed);
      }
    };

    const onFocusOut = (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches("input, textarea, [contenteditable='true'], select")) {
        // Đợi 1 frame rồi kiểm tra trạng thái bàn phím
        requestAnimationFrame(maybeUnfix);
      }
    };

    // Lắng nghe thay đổi viewport do bàn phím mở/đóng/đang animate
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", () => {
        if (isKbOpen()) updateOffset();
        else maybeUnfix();
      });
      window.visualViewport.addEventListener("scroll", () => {
        if (isKbOpen()) updateOffset();
      });
      window.addEventListener("orientationchange", () => {
        // sau xoay màn hình, cập nhật lại offset
        setTimeout(() => {
          if (isKbOpen()) updateOffset();
          else maybeUnfix();
        }, 250);
      });
    }

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", () => {});
        window.visualViewport.removeEventListener("scroll", () => {});
        window.removeEventListener("orientationchange", () => {});
      }
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  return ref;
}
